import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { singular } from 'pluralize';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useArrayField } from "../../hooks/use-array-field.js";
import { useFormStore, useForms } from "../../hooks/use-form-store.js";
import { useSchemaContext } from "../../hooks/use-schema-context.js";
import { generateDiscriminatedDefault } from "../../utils/defaults.js";
import { formatFieldTitle, getPropertyName } from "../../utils/path.js";
import { getShapeTitle } from "../../utils/refs.js";
import { getDiscriminatorOptions, getDiscriminatorProperty, resolveRef } from "../../utils/schema-context.js";
// biome-ignore lint/nursery/noImportCycles: Intentional cycle for recursive form rendering (ArrayField -> default-widgets -> ArrayField)
import { isPrimitiveSchema } from "../../widgets/default-widgets.js";
// biome-ignore lint/nursery/noImportCycles: Intentional cycle for recursive form rendering (ArrayField -> ShapeField -> ArrayField)
import { ShapeField } from "../shape-field.js";
/**
 * Renders array items with section header, expand/collapse controls, and add/remove buttons.
 * Each item is rendered via ShapeField with an arrayItem context.
 */
export function ArrayField({ path, schema, config, disabled, readOnly, title, required, error, description }) {
    const ctx = useSchemaContext();
    const store = useFormStore();
    const forms = useForms();
    const resolvedSchema = useMemo(() => resolveRef(ctx, schema), [ctx, schema]);
    const array = useArrayField({ path, schema: resolvedSchema });
    const itemSchema = array.itemSchema;
    const itemSchemaRef = array.itemSchemaRef;
    const resolvedItemSchema = useMemo(() => (itemSchema ? resolveRef(ctx, itemSchema) : undefined), [ctx, itemSchema]);
    const isPrimitive = isPrimitiveSchema(resolvedItemSchema);
    // Get title for add button
    const configTitle = config?.title;
    const arrayTitle = resolvedSchema.title;
    const fieldName = getPropertyName(path);
    const formattedFieldName = formatFieldTitle(fieldName).replace(/_/g, ' ');
    const addItemTitle = (configTitle ? singular(configTitle) : undefined) ??
        (arrayTitle ? singular(arrayTitle) : singular(formattedFieldName));
    // Expand/collapse all handlers
    const expandAll = useCallback(() => {
        for (const item of array.items) {
            store.getState().setCollapsed(item.path, false);
        }
    }, [store, array.items]);
    const collapseAll = useCallback(() => {
        for (const item of array.items) {
            store.getState().setCollapsed(item.path, true);
        }
    }, [store, array.items]);
    const addLabel = `Add ${addItemTitle || 'Item'}`;
    // Check if item schema is a discriminated union
    const discriminatorProperty = resolvedItemSchema ? getDiscriminatorProperty(ctx, resolvedItemSchema) : undefined;
    const discriminatorOptions = useMemo(() => {
        if (!discriminatorProperty || !resolvedItemSchema)
            return [];
        const rawOptions = getDiscriminatorOptions(ctx, resolvedItemSchema, discriminatorProperty);
        return rawOptions.map((opt) => {
            const formTitle = getShapeTitle(forms, opt.ref);
            if (formTitle) {
                return { ...opt, label: formTitle };
            }
            return opt;
        });
    }, [ctx, resolvedItemSchema, discriminatorProperty, forms]);
    const isDiscriminatedUnion = discriminatorOptions.length > 1;
    const singleVariantValue = discriminatorOptions.length === 1 ? discriminatorOptions[0].value : undefined;
    const appendVariant = useCallback((variantValue) => {
        if (!resolvedItemSchema || !discriminatorProperty)
            return;
        const defaultValue = generateDiscriminatedDefault(resolvedItemSchema, discriminatorProperty, variantValue, ctx);
        array.append(defaultValue);
    }, [array, ctx, resolvedItemSchema, discriminatorProperty]);
    const canAdd = !disabled &&
        !readOnly &&
        (resolvedSchema.maxItems === undefined || array.length < resolvedSchema.maxItems);
    // Collapse items by default if configured
    const collapseByDefault = config?.collapseByDefault;
    const initialCollapseApplied = useRef(false);
    useEffect(() => {
        if (collapseByDefault && !initialCollapseApplied.current && array.items.length > 0) {
            initialCollapseApplied.current = true;
            for (const item of array.items) {
                store.getState().setCollapsed(item.path, true);
            }
        }
    }, [collapseByDefault, array.items, store]);
    // Drag-and-drop state
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    const handleDragStart = useCallback((index) => {
        setDraggedIndex(index);
    }, []);
    const handleDragOver = useCallback((e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;
        const position = e.clientY < midpoint ? 'above' : 'below';
        setDropTarget({ index, position });
    }, [draggedIndex]);
    const handleDragEnd = useCallback(() => {
        if (draggedIndex !== null && dropTarget !== null) {
            let targetIndex = dropTarget.index;
            if (dropTarget.position === 'below') {
                targetIndex = dropTarget.index + 1;
            }
            if (draggedIndex < targetIndex) {
                targetIndex -= 1;
            }
            if (draggedIndex !== targetIndex) {
                array.move(draggedIndex, targetIndex);
            }
        }
        setDraggedIndex(null);
        setDropTarget(null);
    }, [draggedIndex, dropTarget, array]);
    const handleDragLeave = useCallback(() => {
        setDropTarget(null);
    }, []);
    const getDropIndicatorClass = useCallback((index) => {
        if (!dropTarget || dropTarget.index !== index)
            return '';
        return dropTarget.position === 'above' ? 'border-t-2 border-primary' : 'border-b-2 border-primary';
    }, [dropTarget]);
    const itemConfig = config?.items;
    const minItems = resolvedSchema.minItems ?? 0;
    // Both primitive and complex items render through ShapeField with arrayItem context
    return (_jsxs("div", { className: "space-y-1 py-1 mb-1", children: [!isPrimitive && title && (_jsxs("div", { className: "flex items-center", children: [_jsxs("span", { className: "text-sm font-semibold text-base-content/85 flex-1", children: [title, required && _jsx("span", { className: "text-error text-xs ml-0.5", children: "*" }), array.length > 0 && (_jsxs("span", { className: "text-base-content/50 ml-1", children: ["\u2014 ", array.length, " item", array.length !== 1 ? 's' : ''] }))] }), array.length > 0 && (_jsxs("div", { className: "flex gap-1", children: [_jsx("button", { type: "button", className: "btn btn-ghost btn-xs", onClick: expandAll, title: "Expand all", "aria-label": "Expand all items", children: _jsxs("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", "aria-hidden": "true", children: [_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M7 8l5-5 5 5" }), _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M7 16l5 5 5-5" })] }) }), _jsx("button", { type: "button", className: "btn btn-ghost btn-xs", onClick: collapseAll, title: "Collapse all", "aria-label": "Collapse all items", children: _jsxs("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", "aria-hidden": "true", children: [_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M7 4l5 5 5-5" }), _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M7 20l5-5 5 5" })] }) })] }))] })), error && _jsx("p", { className: "text-error text-sm mt-1", children: error }), description && !error && _jsx("p", { className: "text-xs text-base-content/60 mt-1", children: description }), array.items.map((item) => (_jsx(ShapeField, { path: item.path, schema: itemSchema ?? {}, schemaRef: itemSchemaRef, config: itemConfig, disabled: disabled, readOnly: readOnly, arrayItem: {
                    index: item.index,
                    canRemove: array.length > minItems,
                    onRemove: () => array.remove(item.index),
                    isDragged: draggedIndex === item.index,
                    dropIndicatorClass: getDropIndicatorClass(item.index),
                    onDragStart: () => handleDragStart(item.index),
                    onDragOver: (e) => handleDragOver(e, item.index),
                    onDragEnd: handleDragEnd,
                    onDragLeave: handleDragLeave
                } }, item.key))), isDiscriminatedUnion ? (_jsx(DiscriminatorAddDropdown, { addLabel: addLabel, options: discriminatorOptions, onAdd: appendVariant, disabled: !canAdd })) : (_jsxs("button", { type: "button", className: "btn btn-soft btn-xs", onClick: () => (singleVariantValue ? appendVariant(singleVariantValue) : array.append()), disabled: !canAdd, children: [_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", strokeWidth: 2, stroke: "currentColor", className: "size-4", "aria-hidden": "true", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M12 4.5v15m7.5-7.5h-15" }) }), addLabel] }))] }));
}
/**
 * Dropdown button for adding discriminated union items.
 */
function DiscriminatorAddDropdown({ addLabel, options, onAdd, disabled }) {
    const [isOpen, setIsOpen] = useState(false);
    const handleSelect = useCallback((value) => {
        onAdd(value);
        setIsOpen(false);
    }, [onAdd]);
    return (_jsxs("div", { className: "dropdown dropdown-top", children: [_jsxs("button", { type: "button", className: "btn btn-soft btn-xs", onClick: () => setIsOpen(!isOpen), disabled: disabled, children: [_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", strokeWidth: 2, stroke: "currentColor", className: "size-4", "aria-hidden": "true", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M12 4.5v15m7.5-7.5h-15" }) }), addLabel, _jsx("svg", { className: "w-3 h-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" }) })] }), isOpen && (_jsx("ul", { className: "dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow border border-base-300", children: options.map((opt) => (_jsx("li", { children: _jsx("button", { type: "button", onClick: () => handleSelect(opt.value), children: opt.label }) }, opt.value))) }))] }));
}
